/**
 * ProductSelector - Standalone product picker
 * Fetches products from API and displays them
 */

import { useState, useEffect, useId, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Search, X, Package, ImageOff } from 'lucide-react';
import { cn } from "@/lib/utils";
import { useSearchProducts, useProduct } from '@/lib/api/products';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback';
import type { ResourceSelectorProps } from './types';

// Strip HTML tags from string
function stripHtml(html: string): string {
  if (!html) return '';
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function ProductSelector({
  value,
  onChange,
  storeId,
  data: injectedData,
  loading: injectedLoading,
  searchable = true,
  placeholder = 'Search products...',
}: ResourceSelectorProps) {
  const instanceId = useId();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounced search update
  const updateDebouncedSearch = useDebouncedCallback((query: string) => {
    setDebouncedSearch(query);
  }, 300);

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    updateDebouncedSearch(query);
  };

  // Fetch selected product separately (for initialization when it's not in the recent list)
  const { data: selectedProductData, isLoading: isLoadingSelected } = useProduct(
    value || '',
    !!value && !!storeId && !debouncedSearch,
  );

  // Load all products on mount when storeId is provided
  const { data: allProductsData, isLoading: isLoadingAll } = useSearchProducts(
    {
      storeId: storeId || null,
      productId: null,
      searchValue: null,
    },
    { enabled: !!storeId && !debouncedSearch },
  );

  // Use server-side search when there's a search query and storeId
  const shouldSearch = !!storeId && !!debouncedSearch && debouncedSearch.trim().length > 0;

  const { data: searchResults, isLoading: isSearching } = useSearchProducts(
    {
      storeId: storeId || null,
      productId: null,
      searchValue: debouncedSearch || null,
    },
    { enabled: shouldSearch },
  );

  // Determine which products to display
  let allProducts: any[] = injectedData || [];
  let loading = injectedLoading || false;
  const DISPLAY_LIMIT = 100; // Client-side limit for performance

  if (storeId) {
    if (shouldSearch) {
      // User is searching
      allProducts = searchResults || [];
      loading = isSearching;
    } else {
      // Show all products
      allProducts = allProductsData || [];
      loading = isLoadingAll || isLoadingSelected;
    }
  }

  // Client-side filtering and limiting with selected product injection
  const products = useMemo(() => {
    // If there's a selected value, inject it at the top if not already in the list
    if (value && selectedProductData) {
      const isAlreadyInList = allProducts.some((p) => p.id === value);

      if (!isAlreadyInList) {
        // Selected product is NOT in the list (e.g., product #345 out of 500)
        // Inject it at the top, then show others up to limit
        const others = allProducts.slice(0, DISPLAY_LIMIT - 1);
        return [selectedProductData, ...others];
      } else {
        // Selected product IS in the list, move it to top
        const selectedProduct = allProducts.find((p) => p.id === value);
        const others = allProducts.filter((p) => p.id !== value).slice(0, DISPLAY_LIMIT - 1);
        return [selectedProduct, ...others];
    }
  }

    // No selected value, just show first N products
    return allProducts.slice(0, DISPLAY_LIMIT);
  }, [allProducts, value, selectedProductData]);

  const hasMoreResults = allProducts.length > DISPLAY_LIMIT;

  // Auto-scroll to selected item
  useEffect(() => {
    if (value && products.length > 0) {
      setTimeout(() => {
        const selectedElement = document.querySelector(`[data-state="checked"][value="${value}"]`);
        if (selectedElement) {
          selectedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [value, products]);

  // Only show skeletons on INITIAL load (no data yet)
  // This prevents the "accordion effect" when refreshing with existing data
  if (loading && products.length === 0) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse rounded-md border p-3">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded bg-gray-200" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-48 rounded bg-gray-200" />
                <div className="h-3 w-32 rounded bg-gray-200" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 space-y-2 overflow-hidden">
      {searchable && (
        <div className="relative">
          <Search className="text-muted-foreground pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2" />
          <Input
            type="text"
            placeholder={placeholder}
            value={searchQuery}
            onChange={handleSearchChange}
            className="h-8 pl-8 pr-8 text-xs"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery('');
                setDebouncedSearch('');
              }}
              className="text-muted-foreground hover:text-foreground absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 transition-colors"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}

      {/* Results count */}
      {debouncedSearch && products.length > 0 && (
        <div className="text-muted-foreground flex items-center gap-1.5 text-[10px]">
          <Package className="h-3 w-3" />
          <span>{products.length} found</span>
        </div>
      )}

      {/* More results available notice */}
      {!debouncedSearch && !value && hasMoreResults && (
        <div className="rounded-md border border-blue-200 bg-blue-50 px-2 py-1.5 dark:border-blue-900 dark:bg-blue-950">
          <p className="text-[10px] text-blue-700 dark:text-blue-300">
            <strong>
              Showing {DISPLAY_LIMIT} of {allProducts.length} products.
            </strong>{' '}
            Use search to find more.
          </p>
        </div>
      )}

      <RadioGroup name={`product-selector-${instanceId}`} value={value} onValueChange={onChange}>
        <div className="h-[400px] space-y-1.5 overflow-y-auto p-1.5">
          {products.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-md border border-dashed p-4">
              <Package className="text-muted-foreground mb-1 h-5 w-5" />
              <p className="text-muted-foreground text-xs">{debouncedSearch ? 'No match' : 'No products'}</p>
            </div>
          ) : (
            products.map((product: any) => {
              // Get image from: product.imageUrl OR product.image OR first variant's imageUrl
              const productImage =
                product.imageUrl ||
                product.image ||
                product.variants?.find((v: any) => v.imageUrl)?.imageUrl ||
                product.variants?.[0]?.imageUrl ||
                null;

              return (
                <label
                  key={product.id}
                  className={cn(
                    'flex w-full min-w-0 cursor-pointer items-center gap-2 overflow-hidden rounded-md border p-2 transition-all',
                    'hover:border-primary/50 hover:bg-accent',
                    value === product.id && 'border-primary bg-accent ring-primary/20 ring-1',
                  )}
                >
                  <RadioGroupItem value={product.id} className="h-3.5 w-3.5 shrink-0" />
                  {productImage ? (
                    <img
                      src={productImage}
                      alt={product.name}
                      className="h-8 w-8 shrink-0 rounded object-cover"
                    />
                  ) : (
                    <div className="bg-muted flex h-8 w-8 shrink-0 items-center justify-center rounded">
                      <ImageOff className="text-muted-foreground h-3.5 w-3.5" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <h3 className="truncate text-xs font-medium leading-tight">{product.name}</h3>
                    {product.description && (
                      <p className="text-muted-foreground mt-0.5 truncate text-[10px]">
                        {stripHtml(product.description).slice(0, 60)}
                      </p>
                    )}
                  </div>
                </label>
              );
            })
          )}
        </div>
      </RadioGroup>
    </div>
  );
}
